import {
  ciHookDomContracts,
  type CiHook,
  type DataAttr as DataAttrKey,
} from "../../../../.dox/ui";
import { DataAttr, Surface } from "../../../../.dox/ts/unions";

const contractByHook = new Map<CiHook, (typeof ciHookDomContracts)[number]>(
  ciHookDomContracts.map((contract) => [contract.hook, contract]),
);

export const hookDomContract = (
  hook: CiHook,
): (typeof ciHookDomContracts)[number] => {
  const contract = contractByHook.get(hook);
  if (!contract) {
    throw new Error(`missing DOM contract for CI hook ${hook}`);
  }
  return contract;
};

const missingDataAttrs = (
  el: HTMLElement,
  requiredDataAttrs: readonly DataAttrKey[],
): DataAttrKey[] =>
  requiredDataAttrs.filter((key) => el.dataset[key] === undefined);

export const assertHookDomContract = (
  el: HTMLElement,
  hook: CiHook,
): (typeof ciHookDomContracts)[number] => {
  const contract = hookDomContract(hook);
  const missing = missingDataAttrs(el, contract.requiredDataAttrs);
  if (missing.length > 0) {
    throw new Error(
      `${hook} hook missing required data attrs: ${missing.join(", ")}`,
    );
  }
  return contract;
};

export const requireHookCanvas = (
  el: HTMLElement,
  hook: CiHook,
): HTMLCanvasElement => {
  const contract = assertHookDomContract(el, hook);
  if (contract.surface !== Surface.canvas) {
    throw new Error(`${hook} hook contract requires ${contract.surface}`);
  }
  const canvas = el.querySelector("canvas");
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error(`${hook} hook requires a <canvas> child`);
  }
  return canvas;
};

export const readPositiveNumberData = (
  el: HTMLElement,
  hook: CiHook,
  key: DataAttrKey,
): number => {
  const contract = assertHookDomContract(el, hook);
  if (!contract.requiredDataAttrs.includes(key)) {
    throw new Error(`${hook} hook contract does not declare data attr ${key}`);
  }
  const raw = el.dataset[key];
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export { DataAttr, Surface };
